/**
 * Heures des réservations de salle — conversions fuseau de la salle ⇄ instant.
 *
 * En base, `starts_at` / `ends_at` sont des `timestamptz` (instants). Chaque salle
 * porte un fuseau IANA (`rooms.timezone`) : on SAISIT et on AFFICHE toujours les
 * heures dans ce fuseau, jamais dans celui du navigateur. Une salle parisienne
 * consultée depuis Montréal affiche donc 09:00 pour une réunion de 09:00 à Paris.
 *
 * Tout passe par `Intl.DateTimeFormat` : aucune dépendance de fuseau ajoutée.
 *
 * Changements d'heure (même règle que Postgres, pour que front et base tombent
 * d'accord) :
 * - heure murale inexistante (passage à l'heure d'été, 02:30 à Paris) → décalée
 *   d'une heure (03:30) ;
 * - heure murale ambiguë (passage à l'heure d'hiver, 02:30 existe deux fois) →
 *   seconde occurrence, heure d'hiver.
 */

export const DEFAULT_ROOM_TIMEZONE = 'Europe/Paris';

export interface WallTime {
  /** YYYY-MM-DD dans le fuseau */
  date: string;
  /** HH:mm dans le fuseau */
  time: string;
  /** Minutes écoulées depuis 00:00 (horloge murale) */
  minutes: number;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatterCache.set(timeZone, f);
  }
  return f;
}

interface Parts { year: number; month: number; day: number; hour: number; minute: number; second: number }

function partsInZone(epochMs: number, timeZone: string): Parts {
  const out: Record<string, number> = {};
  for (const p of formatter(timeZone).formatToParts(new Date(epochMs))) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return {
    year: out['year'],
    month: out['month'],
    day: out['day'],
    hour: out['hour'] === 24 ? 0 : out['hour'],
    minute: out['minute'],
    second: out['second'],
  };
}

/** Décalage (ms) du fuseau à l'instant donné : heure murale − UTC. */
function offsetMs(epochMs: number, timeZone: string): number {
  const p = partsInZone(epochMs, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(epochMs / 1000) * 1000;
}

const pad = (n: number) => n.toString().padStart(2, '0');

/** Vrai si le navigateur connaît ce fuseau IANA. */
export function isValidTimezone(timeZone: string | null | undefined): boolean {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** Fuseau sûr : celui demandé s'il est valide, sinon Europe/Paris. */
export function safeTimezone(timeZone: string | null | undefined): string {
  return isValidTimezone(timeZone) ? timeZone! : DEFAULT_ROOM_TIMEZONE;
}

/**
 * Heure murale (date YYYY-MM-DD + heure HH:mm) dans `timeZone` → instant.
 * Renvoie un ISO UTC (`…Z`), prêt pour une colonne timestamptz.
 */
export function wallTimeToIso(date: string, time: string, timeZone: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  const wallMs = Date.UTC(y, m - 1, d, hh, mm);
  // Deux passes : le décalage dépend de l'instant qu'on cherche.
  const firstGuess = wallMs - offsetMs(wallMs, timeZone);
  const secondOffset = offsetMs(firstGuess, timeZone);
  return new Date(wallMs - secondOffset).toISOString();
}

/** Instant (ISO, avec ou sans Z / offset) → heure murale dans `timeZone`. */
export function isoToWallTime(iso: string, timeZone: string): WallTime {
  const p = partsInZone(new Date(iso).getTime(), timeZone);
  return {
    date: `${p.year}-${pad(p.month)}-${pad(p.day)}`,
    time: `${pad(p.hour)}:${pad(p.minute)}`,
    minutes: p.hour * 60 + p.minute,
  };
}

/** HH:mm dans le fuseau de la salle. */
export function formatTimeInZone(iso: string, timeZone: string): string {
  return isoToWallTime(iso, timeZone).time;
}

/** Date du jour (YYYY-MM-DD) dans le fuseau. */
export function todayInZone(timeZone: string, now: Date = new Date()): string {
  return isoToWallTime(now.toISOString(), timeZone).date;
}

/** Minutes écoulées depuis minuit, maintenant, dans le fuseau. */
export function nowMinutesInZone(timeZone: string, now: Date = new Date()): number {
  return isoToWallTime(now.toISOString(), timeZone).minutes;
}

/** Ajoute n jours à une date calendaire YYYY-MM-DD (sans fuseau). */
export function addDaysToDate(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/**
 * Bornes [début, fin) en instants d'une plage de jours calendaires du fuseau.
 * `endDateExclusive` = lendemain du dernier jour voulu.
 * Un jour de changement d'heure dure 23 h ou 25 h : c'est voulu.
 */
export function rangeBoundsInZone(startDate: string, endDateExclusive: string, timeZone: string): { start: string; end: string } {
  return {
    start: wallTimeToIso(startDate, '00:00', timeZone),
    end: wallTimeToIso(endDateExclusive, '00:00', timeZone),
  };
}

/** Bornes [début, fin) d'une journée du fuseau. */
export function dayBoundsInZone(date: string, timeZone: string): { start: string; end: string } {
  return rangeBoundsInZone(date, addDaysToDate(date, 1), timeZone);
}

/**
 * Bornes larges couvrant une plage de jours pour PLUSIEURS fuseaux (vues
 * multi-salles / agenda) : la requête prend l'union, le tri par jour se fait
 * ensuite salle par salle dans son propre fuseau.
 */
export function rangeBoundsForZones(startDate: string, endDateExclusive: string, timeZones: string[]): { start: string; end: string } {
  const zones = timeZones.length ? Array.from(new Set(timeZones)) : [DEFAULT_ROOM_TIMEZONE];
  const bounds = zones.map(z => rangeBoundsInZone(startDate, endDateExclusive, z));
  return {
    start: bounds.reduce((min, b) => (b.start < min ? b.start : min), bounds[0].start),
    end: bounds.reduce((max, b) => (b.end > max ? b.end : max), bounds[0].end),
  };
}

/** Fuseau du navigateur (pour signaler un écart avec celui de la salle). */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_ROOM_TIMEZONE;
  } catch {
    return DEFAULT_ROOM_TIMEZONE;
  }
}

/** Liste des fuseaux IANA proposés dans les réglages (repli si le navigateur ne la fournit pas). */
export function availableTimezones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  const list = intl.supportedValuesOf?.('timeZone') ?? [];
  const base = list.length ? list : [
    'Europe/Paris', 'Europe/London', 'Europe/Brussels', 'Europe/Zurich', 'Europe/Madrid',
    'Europe/Berlin', 'Europe/Lisbon', 'America/Montreal', 'America/New_York', 'America/Martinique',
    'America/Guadeloupe', 'America/Cayenne', 'Indian/Reunion', 'Indian/Mayotte', 'Pacific/Noumea',
    'Pacific/Tahiti', 'Africa/Casablanca', 'Africa/Abidjan', 'Africa/Dakar', 'UTC',
  ];
  return base.includes(DEFAULT_ROOM_TIMEZONE) ? base : [DEFAULT_ROOM_TIMEZONE, ...base];
}
